/**
 * File System Access 경계. 받은 바이트를 사용자가 고른 폴더 아래에 원본 트리 구조대로 쓴다.
 *
 * 이 경로가 필요한 이유는 anchor 다운로드로는 디렉터리를 복원할 수 없기 때문이다. 브라우저가
 * 파일명에서 경로 구분자를 제거하므로 `cam0/episode_000001.mp4` 와 `cam1/episode_000001.mp4` 가
 * 같은 이름으로 충돌한다(`@/utils` triggerBrowserDownload 참조).
 */

// TS 5.9 lib.dom 에 FileSystemDirectoryHandle 계열은 있지만 showDirectoryPicker 가 없다.
// File System Access 경계를 이 모듈이 소유하므로 필요한 만큼만 여기서 선언한다.
declare global {
  interface Window {
    showDirectoryPicker?: (options?: {
      mode?: "read" | "readwrite";
      id?: string;
    }) => Promise<FileSystemDirectoryHandle>;
  }
}

export type DirectoryWriter = {
  /**
   * 이미 같은 크기로 받은 파일인가. 중단된 다운로드를 이어받을 때 쓰는 판정이다.
   *
   * 크기를 모르면(`sizeBytes === null`) 판정할 수 없으므로 다시 받는다. 중간에 끊긴 파일은 크기가
   * 다르므로 여기서 걸린다. 그래서 부분 파일이 그대로 남지 않는다.
   */
  isAlreadyWritten: (relativePath: string, sizeBytes: number | null) => Promise<boolean>;
  write: (relativePath: string, body: ReadableStream<Uint8Array>) => Promise<void>;
};

/** 대량 다운로드를 지원하는 브라우저인가(Chromium 계열). 아니면 표면을 노출하지 않는다. */
export function supportsDirectoryDownload(): boolean {
  return typeof window !== "undefined" && typeof window.showDirectoryPicker === "function";
}

/**
 * 저장할 폴더를 고르게 한다. 사용자가 취소하면 null.
 *
 * **반드시 클릭 핸들러 안에서 동기적으로 호출해야 한다.** transient user activation 을 요구하므로
 * 앞에서 await 을 한 번이라도 거치면 activation 이 소실되어 실패한다.
 */
export async function pickDownloadDirectory(): Promise<FileSystemDirectoryHandle | null> {
  const picker = window.showDirectoryPicker;
  if (picker === undefined) return null;
  try {
    return await picker({ mode: "readwrite" });
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") return null;
    throw error;
  }
}

export function createDirectoryWriter(root: FileSystemDirectoryHandle): DirectoryWriter {
  // 폴더 경로 → 핸들. **promise 를 캐시한다.** 동시 다운로드가 같은 폴더를 함께 만들 때
  // 핸들을 캐시하면 생성이 중복된다. 그래서 in-flight 상태를 공유해야 한다.
  const directories = new Map<string, Promise<FileSystemDirectoryHandle>>();

  function directoryFor(segments: readonly string[]): Promise<FileSystemDirectoryHandle> {
    const key = segments.join("/");
    if (key === "") return Promise.resolve(root);
    const cached = directories.get(key);
    if (cached !== undefined) return cached;
    const created = directoryFor(segments.slice(0, -1)).then((parent) =>
      parent.getDirectoryHandle(segments[segments.length - 1], { create: true }),
    );
    directories.set(key, created);
    return created;
  }

  async function fileHandle(relativePath: string, create: boolean): Promise<FileSystemFileHandle> {
    const segments = relativePath.split("/");
    const directory = await directoryFor(segments.slice(0, -1));
    return directory.getFileHandle(segments[segments.length - 1], { create });
  }

  return {
    async isAlreadyWritten(relativePath, sizeBytes) {
      if (sizeBytes === null) return false;
      try {
        const handle = await fileHandle(relativePath, false);
        const file = await handle.getFile();
        return file.size === sizeBytes;
      } catch {
        // NotFoundError 를 포함해 읽을 수 없으면 아직 받지 않은 것으로 본다.
        return false;
      }
    },
    async write(relativePath, body) {
      const handle = await fileHandle(relativePath, true);
      const writable = await handle.createWritable();
      // 실패하면 pipeTo 가 목적지를 abort 한다. 그래서 부분 파일은 크기가 다른 상태로 남는다.
      // 다음 실행의 isAlreadyWritten 이 그 파일을 걸러 다시 받는다.
      await body.pipeTo(writable);
    },
  };
}
